import type { ApplicationTargetResolution } from './applicationTargetResolver'
import { resolveApplicationTarget } from './applicationTargetResolver'

type OptionValue = {
	label: string
	value: string
	platform?: {
		owner?: string
		email?: string
	}
}

export type ApplicationTargetOption = OptionValue & {
	description?: string
	disabled: boolean
	resolution: ApplicationTargetResolution
}

type TagValue =
	| string
	| {
			label?: string
			value?: string
	  }

type GcpProjectLike = {
	type?: string
	details?: Record<string, any>
	tags?: {
		tagsID?: string
		details?: Record<string, any>
	}
}

const applicationEnvironmentOptions = [
	{ label: 'Development', value: 'dev' },
	{ label: 'Staging', value: 'staging' },
	{ label: 'Production', value: 'prod' },
	{ label: 'Sandbox', value: 'sbx' }
]

const applicationViewerEmailPattern = /^[a-z0-9._%+-]+@globe\.com\.ph$/i

const applicationTargetDerivedResetValues = {
	platformName: '',
	platformAlias: '',
	portfolio: '',
	infrastructureType: '',
	deployment: '',
	environment: '',
	platformOwner: '',
	platformOwnerEmail: '',
	applicationViewer: [] as string[]
}

const applicationDetailsResetValues = {
	gcpProjectID: '',
	...applicationTargetDerivedResetValues
}

export const getApplicationResetValues = () => ({
	...applicationDetailsResetValues,
	applicationViewer: [...applicationDetailsResetValues.applicationViewer]
})

export const getApplicationTargetDerivedResetValues = () => ({
	...applicationTargetDerivedResetValues,
	applicationViewer: [...applicationTargetDerivedResetValues.applicationViewer]
})

// {label: Foo, value: fooValue} -> returns the LABEL
export const getDisplayValue = (value: any) => value?.label || value?.value || value || ''

// {label: Foo, value: fooValue} -> returns the VALUE
export const getSelectionValue = (value: any) => value?.value

export const normalizeApplicationViewers = (value: unknown): string[] => {
	if (Array.isArray(value)) {
		return value.map((email) => String(email ?? '').trim())
	}

	if (typeof value === 'string') {
		return value.split(',').map((email) => email.trim())
	}

	return []
}

export const validateApplicationViewers = (value: unknown) => {
	const filledViewers = normalizeApplicationViewers(value).filter(Boolean)

	if (filledViewers.length === 0) {
		return 'Please add at least one application viewer email address.'
	}

	const hasInvalidViewer = filledViewers.some(
		(email) => !applicationViewerEmailPattern.test(email)
	)

	if (hasInvalidViewer) {
		return 'Please enter valid email addresses ending with @globe.com.ph.'
	}

	return true
}

const getTagValue = (value?: TagValue) => {
	if (typeof value === 'string') {
		return value
	}

	return value?.label || value?.value || ''
}

const isRecord = (value: unknown): value is Record<string, any> =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getNestedProjectDetails = (project?: GcpProjectLike) => {
	const details = isRecord(project?.details) ? project?.details : {}
	const projectType = String(project?.type ?? '').trim()
	const nestedDetails = projectType ? details?.[projectType] : undefined

	return isRecord(nestedDetails) ? nestedDetails : {}
}

// Mapping the Environment Type from GCP Project to Application Required Environment Value
const getApplicationEnvironmentOption = (type?: string) => {
	const environmentType = type?.toLowerCase()

	const environmentValue =
		environmentType === 'develop' || environmentType === 'development'
			? 'dev'
			: environmentType === 'staging' || environmentType === 'stage' || environmentType === 'stg'
			? 'staging'
			: environmentType === 'production' || environmentType === 'prd'
			? 'prod'
			: environmentType === 'sandbox'
			? 'sbx'
			: environmentType

	// Return an object that maps to the Normalized Label but retain the value
	return (
		applicationEnvironmentOptions.find(
			(environment) => environment.value === environmentValue
		) || null
	)
}

const getEnvironmentOption = (value?: string) => {
	if (!value) {
		return null
	}

	return getApplicationEnvironmentOption(value) || { label: value, value }
}

const getNonGkeEnvironmentOption = (project?: GcpProjectLike) => {
	const nestedDetails = getNestedProjectDetails(project)
	const details = project?.details || {}
	const environment =
		details.environment ||
		details.env ||
		nestedDetails.environment ||
		nestedDetails.env ||
		project?.type

	return getEnvironmentOption(environment)
}

const getProjectOwnerEmail = (
	project: GcpProjectLike | undefined,
	tagDetails: Record<string, any> = {}
) => {
	const nestedDetails = getNestedProjectDetails(project)

	return (
		getTagValue(tagDetails.platformOwnerEmail) ||
		project?.details?.platformOwnerEmail ||
		nestedDetails.platformOwnerEmail ||
		''
	)
}

const getApplicationTargetDescription = (
	resolution: ApplicationTargetResolution
) => {
	if (resolution.status !== 'ready') {
		return resolution.message || 'Unavailable'
	}

	if (resolution.supportsGke && resolution.resolvedCluster) {
		return 'GKE / Non-GKE'
	}

	return resolution.supportsGke
		? 'Non-GKE; GKE requires an approved cluster'
		: 'Non-GKE'
}

export const buildApplicationTargetOptions = (
	projects: GcpProjectLike[] = [],
	approvedClusters: any[] = []
): ApplicationTargetOption[] =>
	(Array.isArray(projects) ? projects : []).map((project: any) => {
		const resolution = resolveApplicationTarget(project, approvedClusters)
		const tagDetails = resolution.inheritedTagDetails || {}
		const hasPlatformOwner = Boolean(
			getTagValue(tagDetails.platformOwner).trim() &&
				getProjectOwnerEmail(project, tagDetails).trim()
		)

		return {
			label: project.gcpProjectName || project.gcpProjectID || '',
			value: project.gcpProjectID || '',
			description:
				resolution.status === 'ready' && !hasPlatformOwner
					? 'Unavailable: incomplete Platform Owner name or email'
					: getApplicationTargetDescription(resolution),
			disabled: resolution.status !== 'ready' || !hasPlatformOwner,
			resolution
		}
	})

export const getActiveApplicationTargetOption = (
	options: ApplicationTargetOption[] = [],
	value: any
) => {
	const selectedValue = getSelectionValue(value)

	if (!selectedValue) {
		return null
	}

	return (
		(Array.isArray(options) ? options : []).find(
			(option) =>
				option.value === selectedValue &&
				!option.disabled &&
				option.resolution.status === 'ready'
		) || null
	)
}

export const buildApplicationTargetPrefill = (
	resolution: ApplicationTargetResolution
) => {
	const selectedProject = resolution.selectedProject as
		| GcpProjectLike
		| undefined
	const tagDetails = resolution.inheritedTagDetails || {}
	const ownerName = getTagValue(tagDetails.platformOwner)
	const ownerEmail = getProjectOwnerEmail(selectedProject, tagDetails)
	const platformAlias = getTagValue(tagDetails.systemAlias)
	const portfolio = resolution.portfolio || ''

	if (resolution.resolvedCluster) {
		const cluster = resolution.resolvedCluster || {}
		const environment = getEnvironmentOption(cluster.environment) || {label: cluster.environment, value: cluster.environment}

		return {
			deployment: resolution.deployment || '',
			environment,
			portfolio,
			platformName: {
				label: cluster.platformName || '',
				value: cluster.platformID || '',
				platform: {
					owner: ownerName,
					email: ownerEmail
				}
			},
			platformAlias,
			platformOwner: ownerName,
			platformOwnerEmail: ownerEmail,
			applicationViewer: [...resolution.applicationViewer]
		}
	}

	return {
		deployment: resolution.deployment || '',
		environment: getNonGkeEnvironmentOption(selectedProject),
		portfolio,
		platformName: '',
		platformAlias,
		platformOwner: ownerName,
		platformOwnerEmail: ownerEmail,
		applicationViewer: [...resolution.applicationViewer]
	}
}
